import { motion } from 'framer-motion';
import { SearchX } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';

interface DomainUnreachableNoticeProps {
  urlHost: string;
  onCorrect: () => void;
  onProceed: () => void;
  onSwitchToManual: () => void;
}

const containerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
};

// The confirm sub-step fades+slides in under the primary CTA so revealing it
// reads as a deliberate reveal rather than a hard layout snap.
const confirmVariants = {
  hidden: { opacity: 0, y: -4 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.18, ease: 'easeOut' } },
};

/**
 * Shown when the client-side DoH check finds the typed domain does not exist
 * (definitive NXDOMAIN). "Fix the URL" is the prominent path. Proceeding anyway
 * is intentionally a two-step action — a low-emphasis link reveals a final
 * confirm button — so a user can override a false positive (e.g. a brand-new
 * domain still propagating) but is not invited to blow past a real typo.
 */
export const DomainUnreachableNotice = ({
  urlHost,
  onCorrect,
  onProceed,
  onSwitchToManual,
}: DomainUnreachableNoticeProps) => {
  const { t } = useTranslation('getting_started');
  const [confirmingProceed, setConfirmingProceed] = useState(false);

  return (
    <motion.div
      layout
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="flex flex-col gap-6 bg-card border border-border rounded-2xl p-6"
      data-testid="gs-domain-unreachable-notice"
      role="alertdialog"
      aria-labelledby="gs-domain-unreachable-notice-title"
    >
      <div className="flex flex-col gap-4">
        {/* Soft amber anchor: a "couldn't find" caution, not a hard error
            (the destructive red is reserved for the override confirm below). */}
        <span className="flex size-11 items-center justify-center rounded-2xl bg-warning-muted">
          <SearchX className="size-5 text-warning" />
        </span>
        <div className="flex flex-col gap-1.5">
          <h2
            id="gs-domain-unreachable-notice-title"
            className="text-xl font-semibold tracking-tight text-foreground text-balance"
          >
            {t('domain_unreachable_notice.title', { host: urlHost })}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t('domain_unreachable_notice.description', { host: urlHost })}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Button
          type="button"
          size="md"
          onClick={onCorrect}
          data-testid="gs-domain-unreachable-notice-correct"
          className="w-full"
        >
          {t('domain_unreachable_notice.correct_cta')}
        </Button>

        {confirmingProceed ? (
          <motion.div
            variants={confirmVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col gap-3 rounded-xl border border-border bg-muted/50 p-4"
            data-testid="gs-domain-unreachable-notice-confirm"
          >
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t('domain_unreachable_notice.confirm_question', { host: urlHost })}
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setConfirmingProceed(false)}
                className="!text-muted-foreground hover:!text-foreground"
                data-testid="gs-domain-unreachable-notice-cancel"
              >
                {t('domain_unreachable_notice.confirm_cancel')}
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={onProceed}
                data-testid="gs-domain-unreachable-notice-proceed"
              >
                {t('domain_unreachable_notice.confirm_proceed', { host: urlHost })}
              </Button>
            </div>
          </motion.div>
        ) : (
          <div className="flex items-center justify-center gap-5 pt-0.5">
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={onSwitchToManual}
              data-testid="gs-domain-unreachable-notice-manual"
              className="!text-muted-foreground hover:!text-foreground"
            >
              {t('domain_unreachable_notice.secondary_cta_manual_setup')}
            </Button>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => setConfirmingProceed(true)}
              data-testid="gs-domain-unreachable-notice-proceed-anyway"
              className="!text-foreground/45 hover:!text-foreground/70"
            >
              {t('domain_unreachable_notice.proceed_anyway_cta')}
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};
